// ViewErrorBoundary — stops one screen's render error from blanking the whole app.
//
// Why (2026-08-02): a `console.warn` template in product-resolver.js referenced a
// parameter that had been renamed, so it threw a ReferenceError instead of warning.
// That happened inside resolveComps, inside ProductsView's render, with no boundary
// anywhere — React unmounted the entire tree. Alex saw the Product screen appear for
// under a second and then go white, with no clue what had happened. The message was
// only in DevTools, and the branch that reached it needed a product with hours and no
// labour rate, which had not existed until that morning.
//
// A boundary would not have prevented the bug. It would have turned "the app is
// broken" into "this screen failed, here is why, the rest still works" — which is the
// difference between a five-minute fix and a debugging session.
//
// Deliberately a class component: `getDerivedStateFromError` / `componentDidCatch`
// have no hook equivalent, and React has never provided one.
//
// Props:
//   name      — the screen's name, shown in the message ("Products")
//   onReset    — optional; called when the user clicks "Try again"
//   onEscape   — optional; a way OUT of the failed view. Needed because the nav
//                switcher lives inside each view and dies with it.
//   children

import { Component } from 'react';
import { BTN_PRIMARY, BTN_SECONDARY } from '../theme-tokens.js';

export default class ViewErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the full detail in the console — the boundary summarises, it does not hide.
    console.error(`[${this.props.name || 'view'}] render failed:`, error, info?.componentStack);
  }

  // A different screen (or a different row/product) should get a clean attempt rather
  // than inheriting the previous failure.
  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const { name = 'This screen' } = this.props;
    return (
      <div style={{ padding: 24, maxWidth: 720 }}>
        <div style={{ fontSize: 'var(--fs-body)', fontWeight: 700, color: 'var(--th-text-body)', marginBottom: 8 }}>
          {name} could not be displayed.
        </div>
        {/* The nav switcher lives INSIDE each view (ViewSwitcher is passed as
            HierarchyWorkspace's treeHeader), so a crashed view takes the way out with
            it — verified. The escape hatch has to be here, or the user is stuck with
            only the browser Back button. */}
        <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)', marginBottom: 12 }}>
          Only this screen failed — your data is untouched. The full error and its stack
          are in the browser console (F12).
        </div>
        <pre style={{
          fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)',
          background: 'var(--th-row-even)', border: '1px solid var(--th-border)',
          borderRadius: 3, padding: '8px 10px', overflowX: 'auto',
          color: 'var(--th-text-body)', margin: 0, marginBottom: 12,
        }}>{String(this.state.error?.message || this.state.error)}</pre>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => { this.setState({ error: null }); this.props.onReset?.(); }}
            style={{ ...BTN_PRIMARY, fontWeight: 600 }}
          >Try again</button>
          {this.props.onEscape && (
            <button
              onClick={() => { this.setState({ error: null }); this.props.onEscape(); }}
              style={BTN_SECONDARY}
            >Go to Catalog</button>
          )}
        </div>
      </div>
    );
  }
}
